'use client'

import Link from 'next/link'
import {GuestSignInHint} from './GuestSignInHint'
import {useEffect, useId, useRef, useState, type FormEvent} from 'react'
import {useTranslation} from '../../../hooks/useTranslation'
import {
    commitGuestCheckout,
    GuestCheckoutError,
    isGuestCheckoutCommitRefusal,
    startGuestCheckoutPayment,
    type GuestCheckoutCommit,
    type GuestCheckoutErrorCode,
    type GuestCheckoutPreview,
} from '../../../services/guest-checkout.service'
import {readGuestContactDraft, saveGuestContactDraft} from '../../../lib/guest-contact-draft'
import {focusFirstInvalidGuestField, validateGuestContact, type GuestContactErrors, type GuestContactField} from '../../../lib/guest-contact-validation'
import {usePaymentRedirect} from '../shared/usePaymentRedirect'
import {Button, getButtonClasses} from '@fitro/customer-ui/Button'
import {AlertBanner} from '../../ui'
import {GuestContactFields, type GuestConsents, type GuestContactValues, type GuestGender} from './GuestContactFields'
import {BenefitProviderLogo, hasBenefitProviderWordmark, type BenefitProvider} from '@fitro/customer-ui/BenefitProvider'
import type {GuestBookingFailure} from './PublicGuestBookingChooser'

type Contact = GuestContactValues

const EMPTY_CONTACT: Contact = {firstName: '', lastName: '', email: '', phone: ''}

// The preview behind this form can no longer be booked, but a new one for the same choice can. Its
// price or terms may differ, so the guest checks them and pays again; the form and its data stay.
const RENEW_CODES = new Set<GuestCheckoutErrorCode>(['checkout_expired', 'offer_changed', 'preview_expired', 'terms_changed'])

const PRIMARY_LINK_CLASS_NAME = getButtonClasses({variant: 'primary'})

/**
 * Collects the guest's contact and consents for a previewed Guest Checkout, then commits it and
 * sends the guest to the online payment page. Nothing is booked until the payment succeeds.
 */
export function PublicGuestCheckoutPanel({
    partnerSlug,
    preview,
    benefitProvider = null,
    onCancel,
    onRenew,
    otherTermsHref,
    signInHref,
    waitlistAvailable = false,
    firstEntryMinor = null,
}: {
    partnerSlug: string
    preview: GuestCheckoutPreview
    /** The brand of the declared card, shown as its logo next to the summary. */
    benefitProvider?: BenefitProvider | null
    /** Closes the form. */
    onCancel: () => void
    /** Replaces `preview` with a new one for the same choice; resolves with why none could be made. */
    onRenew: () => Promise<GuestBookingFailure | null>
    /** Where a guest goes when this Term can no longer be booked. */
    otherTermsHref: string
    /** Where a visitor with an account, a pass or a discount code signs in instead. */
    signInHref: string
    /** A full Term still takes signed-in customers on its waitlist. */
    waitlistAvailable?: boolean
    /** The lower price a new customer pays for a first entry after signing in. */
    firstEntryMinor?: number | null
}) {
    const {t, dateTime, formatCurrency} = useTranslation('landing')
    const formId = useId()
    // A contact typed in the last 10 minutes comes back, e.g. after the class filled up.
    const [draft] = useState(readGuestContactDraft)
    const [contact, setContact] = useState<Contact>(() => draft
        ? {firstName: draft.firstName, lastName: draft.lastName, email: draft.email, phone: draft.phone}
        : EMPTY_CONTACT)
    // Asked every time and never kept in the draft; the studio keeps it only for a customer without one.
    const [gender, setGender] = useState<GuestGender>('')
    const [studioTermsAccepted, setStudioTermsAccepted] = useState(false)
    const [fitroTermsAccepted, setFitroTermsAccepted] = useState(false)
    const [marketingConsent, setMarketingConsent] = useState(false)
    const [submitting, setSubmitting] = useState(false)
    const [error, setError] = useState<string | null>(null)
    // The Term cannot be booked without an account or no longer at all: the one step left replaces paying.
    const [nextStep, setNextStep] = useState<Exclude<GuestBookingFailure['next'], 'retry'> | null>(null)
    const [fieldErrors, setFieldErrors] = useState<GuestContactErrors>({})
    const redirectToPayment = usePaymentRedirect(() => setSubmitting(false))
    // One Idempotency-Key per submitted request: a retry of the same data must replay a Hold the
    // server may already have saved (e.g. 503 after the commit), edited data is a new attempt.
    const commitAttempt = useRef<{idempotencyKey: string; request: string} | null>(null)
    // The saved Hold: once the commit succeeded, only the payment start is retried and the form is locked.
    const [held, setHeld] = useState<GuestCheckoutCommit | null>(null)
    const headingRef = useRef<HTMLHeadingElement>(null)
    // The panel replaces the button or the choice that opened it; move focus (and the viewport) to the form.
    useEffect(() => {
        headingRef.current?.focus({preventScroll: true})
        headingRef.current?.scrollIntoView?.({block: 'start'})
    }, [])

    function clearFieldError(field: GuestContactField) {
        setFieldErrors((current) => {
            const next = {...current}
            delete next[field]
            return next
        })
        setError(null)
    }

    function update(field: keyof Contact, value: string) {
        setContact((current) => ({...current, [field]: value}))
        clearFieldError(field)
        // The refusal concerned the email typed before; another one may book without an account.
        if (field === 'email' && nextStep === 'sign_in') setNextStep(null)
    }




    async function submit(event: FormEvent<HTMLFormElement>) {
        event.preventDefault()
        if (held) {
            setSubmitting(true)
            setError(null)
            await startPayment(held)
            return
        }
        const trimmed: Contact = {
            firstName: contact.firstName.trim(),
            lastName: contact.lastName.trim(),
            email: contact.email.trim(),
            phone: contact.phone.trim(),
        }
        const errors = validateGuestContact({
            ...trimmed, gender, genderRequired: preview.genderRequired, studioTermsAccepted, fitroTermsAccepted,
        })
        setFieldErrors(errors)
        if (focusFirstInvalidGuestField(event.currentTarget, errors)) {
            setError(null)
            return
        }
        setSubmitting(true)
        setError(null)
        saveGuestContactDraft({...trimmed})
        const consents = {
            termsAccepted: studioTermsAccepted,
            privacyAccepted: studioTermsAccepted,
            fitroTermsAccepted,
            marketingConsent,
        }
        const guest = preview.genderRequired && gender ? {...trimmed, gender} : trimmed
        const request = JSON.stringify([preview.previewId, guest, consents])
        if (commitAttempt.current?.request !== request) {
            commitAttempt.current = {idempotencyKey: crypto.randomUUID(), request}
        }
        let committed: GuestCheckoutCommit
        try {
            committed = await commitGuestCheckout(
                partnerSlug, preview.previewId, guest, consents, commitAttempt.current.idempotencyKey,
            )
        } catch (failure) {
            if (isGuestCheckoutCommitRefusal(failure)) commitAttempt.current = null
            await fail(failure)
            return
        }
        setHeld(committed)
        await startPayment(committed)
    }

    async function startPayment(committed: GuestCheckoutCommit) {
        try {
            redirectToPayment(await startGuestCheckoutPayment(committed.recoveryToken))
        } catch (failure) {
            await fail(failure)
        }
    }

    async function fail(failure: unknown) {
        const code: GuestCheckoutErrorCode = failure instanceof GuestCheckoutError ? failure.code : 'unavailable'
        if (code === 'login_required' || code === 'full' || code === 'registration_closed') {
            setHeld(null)
            stop(refusal(code))
        } else if (RENEW_CODES.has(code)) {
            setHeld(null)
            const refused = await onRenew()
            if (refused) stop(refused)
            else {
                // Consent to the old terms does not cover the new ones.
                if (code === 'terms_changed') {
                    setStudioTermsAccepted(false)
                    setFitroTermsAccepted(false)
                }
                setError(t(`publicEntry.guestCheckout.errors.${code}`))
            }
        } else {
            setError(t(`publicEntry.guestCheckout.errors.${code}`))
        }
        setSubmitting(false)
    }

    // The server does not say whether this email already has a place on the Term, as anyone may type
    // any email here; the message covers both that and a Term that needs an account.
    function refusal(code: 'login_required' | 'full' | 'registration_closed'): GuestBookingFailure {
        if (code === 'login_required') return {message: t('publicEntry.guestCheckout.commitSignInRequired'), next: 'sign_in'}
        const waitlist = code === 'full' && waitlistAvailable
        return waitlist
            ? {message: t('publicEntry.guestCheckout.fullWaitlist'), next: 'sign_in', waitlist}
            : {message: t(`publicEntry.guestCheckout.errors.${code}`), next: 'other_terms'}
    }

    function stop(failure: GuestBookingFailure) {
        setError(failure.message)
        setNextStep(failure.next === 'retry' ? null : failure.next)
    }

    function updateConsent(field: keyof GuestConsents, checked: boolean) {
        if (field === 'marketingConsent') return setMarketingConsent(checked)
        if (field === 'studioTermsAccepted') setStudioTermsAccepted(checked)
        else setFitroTermsAccepted(checked)
        clearFieldError(field)
    }

    return (
        <section
            data-guest-checkout-panel
            aria-labelledby={`${formId}-title`}
            className="rounded-[12px] border border-[var(--landing-neutral-100)] bg-white p-5 shadow-[0_10px_24px_rgba(57,98,198,0.06)] sm:p-6"
        >
            <h2 ref={headingRef} tabIndex={-1} id={`${formId}-title`} className="sr-only scroll-mt-24">{t('publicEntry.guestCheckout.title')}</h2>
            <form className="space-y-5" onSubmit={(event) => void submit(event)} noValidate>
                <GuestSignInHint
                    href={signInHref}
                    firstEntry={firstEntryMinor !== null && !preview.summary.benefitCardName && firstEntryMinor < preview.summary.amountMinor
                        ? formatCurrency(firstEntryMinor / 100, {currency: preview.summary.currency})
                        : null}
                />
                {/* The price was chosen in the previous step: say only what is booked and what is paid. */}
                <p className="flex items-center justify-between gap-3 text-base text-gray-900 dark:text-gray-100">
                    <span className="flex min-w-0 items-center gap-2 break-words font-medium">
                        {preview.summary.benefitCardName && benefitProvider && hasBenefitProviderWordmark(benefitProvider) ? <BenefitProviderLogo provider={benefitProvider} className="h-6 shrink-0" /> : null}
                        {preview.summary.benefitCardName
                            ? t('publicEntry.guestCheckout.benefitCardOption', {card: preview.summary.benefitCardName})
                            : t('publicEntry.guestCheckout.benefitCardNone')}
                    </span>
                    <span className="shrink-0 font-bold tabular-nums">
                        {formatCurrency(preview.summary.amountMinor / 100, {currency: preview.summary.currency})}
                    </span>
                </p>
                <fieldset disabled={held !== null} className="min-w-0 space-y-5 border-0 p-0">
                    <GuestContactFields
                        idPrefix={formId}
                        contact={contact}
                        onContactChange={update}
                        consents={{studioTermsAccepted, fitroTermsAccepted, marketingConsent}}
                        onConsentChange={updateConsent}
                        errors={fieldErrors}
                        policies={preview}
                        gender={preview.genderRequired ? {value: gender, onChange: (value) => {
                            setGender(value)
                            clearFieldError('gender')
                        }} : null}
                    />
                </fieldset>
                {held ? (
                    <p className="rounded-lg border border-success-200 bg-success-50 p-3 text-sm text-success-700 dark:border-success-800 dark:bg-success-950/30 dark:text-success-300">
                        {t('publicEntry.guestCheckout.placeHeld', {time: dateTime.formatInstantTime(held.holdExpiresAt), email: contact.email.trim()})}
                    </p>
                ) : null}
                {error ? <AlertBanner variant="error" size="small" dismissible={false} message={error} /> : null}
                <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
                    <Button type="button" variant="secondary" onClick={() => onCancel()} disabled={submitting || held !== null}>
                        {t('publicEntry.guestCheckout.cancel')}
                    </Button>
                    {nextStep === 'sign_in' ? (
                        <Link href={signInHref} className={PRIMARY_LINK_CLASS_NAME}>{t('publicEntry.guestCheckout.signIn')}</Link>
                    ) : nextStep === 'other_terms' ? (
                        <Link href={otherTermsHref} className={PRIMARY_LINK_CLASS_NAME}>{t('publicEntry.guestCheckout.otherTerms')}</Link>
                    ) : (
                        <Button type="submit" loading={submitting} disabled={submitting}>
                            {submitting ? t('publicEntry.guestCheckout.processing') : t('publicEntry.guestCheckout.pay')}
                        </Button>
                    )}
                </div>
            </form>
        </section>
    )
}
